import { useEffect, useMemo } from 'react';
import { useSeriesColorGenerator } from '@/components';
import { DistributionChartPropsBase, DistributionDataResponse, HistogramBucket, TimeSeriesData } from '@/core';
import { BasePanelProps, useDataQueryResult, useVizPanelNotice } from '@/plugin-system';
import { convertTimeSeriesDataToHeatMap } from '@/prometheus-plugin/plugins/prometheus-time-series-query/heat-map-utils';
import { DistributionChartBase } from './DistributionChartBase';

export interface DistributionChartPanelProps extends BasePanelProps, DistributionChartPropsBase {
  // Extend values from `DistributionChartPropsBase` below, as needed, to add in jsdoc to help developers.
}

const TARGET_BAR_WIDTH = 30;

const percentile = (arr: number[], p: number) => {
  const idx = Math.ceil(arr.length * (p / 100)) - 1;
  return arr[idx];
};

export const combineSeries = (results: DistributionDataResponse[]) => {
  return results.reduce(
    (acc, result) => {
      if (result.data.distributionKind === 'TimeSeries') {
        acc.push(...(result.data?.series ?? []));
      }
      return acc;
    },
    [] as TimeSeriesData['series']
  );
};

export const combinePoints = (results: DistributionDataResponse[]) => {
  const points: number[] = [];

  for (const result of results) {
    const data = result.data;

    if (data.distributionKind === 'TimeSeries') {
      if (data === undefined || data.series.length === 0) {
        continue;
      }

      for (let i = 0; i < data.series.length; i++) {
        const series = data.series[i];
        if (!series) continue;
        for (let j = 0; j < series.values.length; j++) {
          const value = series.values[j];
          if (!value) continue;
          if (value[1] !== null) {
            points.push(value[1]);
          }
        }
      }
    }
  }

  return points;
};

export function DistributionChartPanel(props: DistributionChartPanelProps) {
  const { setPanelNotice, deletePanelNotice } = useVizPanelNotice('distribution-chart-panel');
  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'Distribution' });

  const timeSeriesData = useMemo(
    () => queryResults.filter((r) => r.data?.distributionKind === 'TimeSeries'),
    [queryResults]
  );
  const histogramData = useMemo(
    () => queryResults.filter((r) => r.data?.distributionKind === 'HeatMap'),
    [queryResults]
  );

  useEffect(() => {
    if (timeSeriesData.length > 0 && histogramData.length > 0) {
      setPanelNotice({
        type: 'warning',
        message: 'Queries resulted in both time series and histogram data. Only histogram data will be shown.',
      });
    } else {
      deletePanelNotice();
    }
  }, [timeSeriesData, histogramData, setPanelNotice, deletePanelNotice]);

  if (queryResults.some((r) => r.isLoading || r.isFetching)) {
    return null;
  }

  if (histogramData.length > 0) {
    return <DistributionChartPanelHistogram {...props} />;
  }

  return <DistributionChartPanelTimeSeries {...props} />;
}

export function DistributionChartPanelHistogram({
  height,
  width,
  unit,
  percentileMarkers,
}: DistributionChartPanelProps) {
  const { setPanelNotice, deletePanelNotice } = useVizPanelNotice('distribution-chart-panel-histogram');
  const { setPanelNotice: setPanelNoticePercentileMarkers, deletePanelNotice: deletePanelNoticePercentileMarkers } =
    useVizPanelNotice('distribution-chart-panel-histogram-percentile-markers');
  const colorGenerator = useSeriesColorGenerator();

  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'Distribution' });

  useEffect(() => {
    if (queryResults.length > 1) {
      setPanelNotice({
        type: 'warning',
        message: 'Queries resulted in multiple histograms.  Only the first histogram result will be shown.',
      });
    } else {
      deletePanelNotice();
    }

    if (percentileMarkers && percentileMarkers.length > 0) {
      setPanelNoticePercentileMarkers({
        type: 'warning',
        message: 'Percentile markers are not supported for histogram data.',
      });
    } else {
      deletePanelNoticePercentileMarkers();
    }
  }, [
    queryResults,
    setPanelNotice,
    deletePanelNotice,
    setPanelNoticePercentileMarkers,
    deletePanelNoticePercentileMarkers,
    percentileMarkers,
  ]);

  // The data is already bucketed, so we can just use the data as is
  const firstResult = queryResults[0];
  if (firstResult && firstResult.data.distributionKind === 'HeatMap') {
    // Loop over every entry in firstResult.data.heatmap and combine all the bucket counts into one
    const buckets: HistogramBucket[] = [];

    for (const entry of firstResult.data.heatmap) {
      for (const bucket of entry.buckets) {
        const matchingBucket = buckets.find(
          (b) => b.lowerBound === bucket.lowerBound && b.upperBound === bucket.upperBound
        );
        if (!matchingBucket) {
          buckets.push({ ...bucket });
        } else {
          matchingBucket.count += bucket.count;
        }
      }
    }

    // Sort buckets by lowerBound to ensure they display in the correct order
    buckets.sort((a, b) => a.lowerBound - b.lowerBound);

    return (
      <DistributionChartBase
        percentiles={[]}
        buckets={buckets}
        primaryColor={colorGenerator.palette.Categorical[0]}
        height={height}
        width={width}
        unit={unit}
      />
    );
  }
}

export function DistributionChartPanelTimeSeries({
  height,
  width,
  percentileMarkers,
  unit,
}: DistributionChartPanelProps) {
  const colorGenerator = useSeriesColorGenerator();

  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'Distribution' });

  const numBuckets = useMemo(() => {
    return Math.floor(width / TARGET_BAR_WIDTH);
  }, [width]);

  const combinedSeriesData = useMemo(() => {
    return combineSeries(queryResults);
  }, [queryResults]);

  const allPoints = useMemo(() => {
    return combinePoints(queryResults);
  }, [queryResults]);

  const { buckets, percentileValues } = useMemo(() => {
    allPoints.sort((a, b) => a - b);
    const pValues = (percentileMarkers ?? [])
      .map((p) => {
        return { value: percentile(allPoints, p), percentile: p };
      })
      .filter<{ value: number; percentile: number }>(
        (p): p is { value: number; percentile: number } => p.value !== undefined
      );

    const timeSeriesData = {
      series: combinedSeriesData,
    };

    const heatmapData = convertTimeSeriesDataToHeatMap(timeSeriesData, numBuckets, () => 0);

    const buckets = heatmapData[0]?.buckets ?? [];

    return { buckets, percentileValues: pValues };
  }, [allPoints, combinedSeriesData, numBuckets, percentileMarkers]);

  return (
    <DistributionChartBase
      percentiles={percentileValues}
      buckets={buckets}
      primaryColor={colorGenerator.palette.Categorical[0]}
      height={height}
      width={width}
      unit={unit}
    />
  );
}
